import { ItemTableConsts } from 'client/util/Constants';
import { get } from 'lodash';
import SystemInsightsConstants from 'client/util/Constants/SystemInsightsConstants';

const { parentOSType } = ItemTableConsts;
const {
  android, darwin, ios, universal, windows,
} = parentOSType;

// --------------------------------------------------------------------------
// Private Helper Methods
// --------------------------------------------------------------------------

/**
 * Returns first partial match to nicName prefix supplied
 */
const findNIC = (item, nicName, sort) => {
  let ret;
  let { networkInterfaces } = item;

  if (networkInterfaces) {
    networkInterfaces = networkInterfaces.filter(i => (
      i.name.indexOf(nicName) === 0 && i.family === 'IPv4'
    ));

    if (sort && networkInterfaces) {
      networkInterfaces.sort((interfaceA, interfaceB) => {
        const re = new RegExp(`^${nicName}([0-9]*)(.*)$`);

        const aResult = re.exec(interfaceA.name);
        const aNumber = aResult[1];
        const aValue = aResult[2];

        const bResult = re.exec(interfaceB.name);
        const bNumber = bResult[1];
        const bValue = bResult[2];

        if ((aNumber && !bNumber)
          || (aNumber && bNumber && parseInt(aNumber, 10) < parseInt(bNumber, 10))) {
          return -1;
        }
        if ((!aNumber && bNumber)
          || (aNumber && bNumber && parseInt(aNumber, 10) > parseInt(bNumber, 10))) {
          return 1;
        }
        if (aNumber && bNumber && parseInt(aNumber, 10) === parseInt(bNumber, 10)) {
          return 0;
        }
        return aValue.localCompare(bValue);
      });
    }
    [ret] = networkInterfaces;
  }
  return (ret && ret.address) ? ret.address : '';
};

/**
 * Returns exact match based on nicName supplied
 */
const getNIC = (item, nicName) => {
  let ret;
  const { networkInterfaces } = item;

  if (networkInterfaces) {
    [ret] = networkInterfaces.filter(i => (
      i.name === nicName && i.family === 'IPv4'
    ));
  }

  return (ret && ret.address) ? ret.address : '';
};

// Splits the IP address into 4 groups e.g. 192.168.0.1 -> [192,168,0,1].
const ipDotNotationRegex = /^([0-9]{1,3})\.([0-9]{1,3})\.([0-9]{1,3})\.([0-9]{1,3})$/;

// expects only valid matches
const compareIpAddress = (a, b) => {
  const aMatch = ipDotNotationRegex.exec(a.address);
  const bMatch = ipDotNotationRegex.exec(b.address);

  // Find the lowest IP address by comparing address segments, left to right
  for (let i = 1; i <= 4; i += 1) {
    if (parseInt(aMatch[i], 10) < parseInt(bMatch[i], 10)) {
      return -1;
    } if (parseInt(aMatch[i], 10) > parseInt(bMatch[i], 10)) {
      return 1;
    }
  }

  return 0;
};

// validating for dot-decimal notation only
const validIpAddress = address => address && ipDotNotationRegex.test(address);

// accounting for dot-decimal notation only
const findLowestIP = (item) => {
  const { networkInterfaces } = item;
  let lowestAddress;

  if (networkInterfaces) {
    [lowestAddress] = networkInterfaces
      .filter(i => i.family === 'IPv4')
      .filter(i => validIpAddress(i.address))
      .sort(compareIpAddress);
  }

  return (lowestAddress && lowestAddress.address) ? lowestAddress.address : '';
};

/**
 * @description This module contains common methods used in the SystemCollection
 * and SystemModel Vuex stores. These methods are called
 */
export default {
  isInBadState(item) {
    return this.isMacOS(item) && (this.hasBadServiceAccountState(item));
  },

  isFdeKeyPresent(item) {
    return !!(item.fde && item.fde.keyPresent);
  },

  hasBadServiceAccountState(item) {
    return (!item.hasServiceAccount
      || !item.serviceAccountState.passwordAPFSValid
      || !item.serviceAccountState.passwordODValid
      || !item.serviceAccountState.hasSecureToken);
  },

  isWindowsDeviceDomainJoined(item) {
    const isDeviceDomainJoined = get(item, 'domainInfo.partOfDomain');
    return (this.isWindows(item) && !!isDeviceDomainJoined);
  },

  isAzureDomainJoined(item) {
    return this.isWindows(item) && !!(item.azureAdJoined && item.azureAdJoined === true);
  },

  isLinux(item) {
    return !!(item.osFamily && !this.isMacOS(item)
      && !this.isWindows(item) && !this.isIOS(item)
      && !this.isUniversalPolicy(item) && !this.isAndroid(item)
    );
  },

  isIOS(item) {
    return !!(item.osFamily && item.osFamily.toLowerCase().includes(ios));
  },

  isMacOS(item) {
    return !!(item.osFamily && item.osFamily.toLowerCase().includes(darwin));
  },

  isWindows(item) {
    return !!(item.osFamily && item.osFamily.toLowerCase().includes(windows));
  },

  isUniversalPolicy(item) {
    return !!(item.osFamily && item.osFamily.toLowerCase().includes(universal));
  },

  isAndroid(item) {
    return !!(item.osFamily && item.osFamily.toLowerCase().includes(android));
  },

  isSystemMdmEnrolled(item) {
    if (!item.mdm) return null;

    // the InternalMDMSchema differs across windows & darwin
    const mdmInternalId = item.osFamily?.toLowerCase() === windows
      ? item.mdm.internal?.windowsDeviceId
      : item.mdm.internal?.deviceId;

    const isMdmJumpcloudEnrolled = (item.mdm
      && item.mdm.vendor
      && item.mdm.vendor === 'internal'
      && item.mdm.internal
      && mdmInternalId);

    if (isMdmJumpcloudEnrolled) return true;
    if (item.mdm.vendor && (item.mdm.vendor === 'external' || item.mdm.vendor === 'none')) return false;
    return null;
  },

  isSystemMdmVendorValid(item) {
    if (item.mdm && (item.mdm.vendor === null || item.mdm.vendor === undefined || item.mdm.vendor === 'unknown')) return null;
    return !!(item.mdm && (item.mdm.vendor === 'internal' || item.mdm.vendor === 'external'));
  },

  isSystemDepEnrolled(item) {
    if (!item.mdm) return null;
    if (item.mdm.dep === undefined) return null;
    return item.mdm.dep;
  },

  isSystemEnrolledWithDifferentVendor(item) {
    const systemEnrolledDifferentVendor = item
      && item.mdm
      && item.mdm.profileIdentifier !== null
      && item.mdm.profileIdentifier !== undefined
      && item.mdm.profileIdentifier !== 'com.jumpcloud.mdm';

    if (systemEnrolledDifferentVendor) return true;

    return false;
  },

  /**
   * Return true if multifactor authentication is supported. Currently, MFA is not supported on
   * Linux systems with SSH password auth and with public key auth.
   */
  isMultifactorSupported(item = {}) {
    if (this.isLinux(item)) {
      const { allowPublicKeyAuthentication, allowSshPasswordAuthentication } = item;

      if (typeof allowPublicKeyAuthentication === 'boolean' && allowPublicKeyAuthentication === true
        && typeof allowSshPasswordAuthentication === 'boolean' && allowSshPasswordAuthentication === true) {
        return false;
      }
    }

    return true;
  },

  isUserApproved(item = {}) {
    if (item.mdm && item.mdm.userApproved == null) return null;

    return !!item.mdm.userApproved;
  },

  getServiceAccountState(item) {
    return ({
      'Has Service Account:': item.hasServiceAccount,
      'Secure Token Granted:': item.serviceAccountState.hasSecureToken,
      'Valid Local Password:': item.serviceAccountState.passwordODValid,
      'Valid Disk Password:': item.serviceAccountState.passwordAPFSValid,
    });
  },

  /**
   * Return the default NIC address for a given system based on its operating system.
   */
  getSystemNIC(item) {
    if (this.isMacOS(item)) {
      return getNIC(item, 'en0') || getNIC(item, 'en1');
    }

    if (this.isWindows(item)) {
      return getNIC(item, 'Ethernet') || getNIC(item, 'Local Area Connection');
    }

    // assume Linux
    // predictable naming conventions first @see https://www.freedesktop.org/software/systemd/man/systemd.net-naming-scheme.html
    return findNIC(item, 'eno', true) // ethernet firmware
      || findNIC(item, 'ens', true) // ethernet slot
      || findNIC(item, 'enp', true) // ethernet domain
      || getNIC(item, 'eth0')
      || getNIC(item, 'eth1')
      || findNIC(item, 'eth0')
      || findNIC(item, 'eth1')
      || findNIC(item, 'wl') // wlan
      || findNIC(item, 'ww') // wwan
      || findNIC(item, 'wlp0')
      || findLowestIP(item);
  },

  showSystemInsights(item, orgSystemInsightsEnabled) {
    // Do not show if the organization does not have System Insights enabled
    if (!orgSystemInsightsEnabled) return false;

    // Check if the system has System Insights enabled
    const { enabled } = SystemInsightsConstants.systemState;
    const { systemInsights } = item;
    return systemInsights && systemInsights.state === enabled;
  },

  getSystemMdmId(item) {
    if (!(this.isSystemMdmEnrolled(item) && item.mdm.internal)) return false;

    return item.mdm.internal.deviceId;
  },

  getSystemMdmProfileIdentifier(item) {
    if (!item.mdm) return null;
    if (!item.mdm.profileIdentifier) return null;
    return item.mdm.profileIdentifier;
  },
};
